import {
  ClientContractHubContractDto,
  ClientContractHubTimelineEventDto,
  EContractStatus,
} from '../../services/contracts/ContractService';
import { normalizeContractStatus } from '../../utils/contractStatusUtils';

export const EMPTY_CONTRACT_ID = '00000000-0000-0000-0000-000000000000';

export const formatHubUtc = (value?: string) =>
  value ? new Date(value).toLocaleString('pt-BR') : '—';

export type HubListItem =
  | { kind: 'contract'; contract: ClientContractHubContractDto }
  | { kind: 'group'; groupId: string; members: ClientContractHubContractDto[] };

export const buildHubListItems = (
  contracts: ClientContractHubContractDto[]
): HubListItem[] => {
  const items: HubListItem[] = [];
  const grouped = new Map<string, ClientContractHubContractDto[]>();

  for (const contract of contracts) {
    if (contract.groupId) {
      const list = grouped.get(contract.groupId) ?? [];
      list.push(contract);
      grouped.set(contract.groupId, list);
    } else {
      items.push({ kind: 'contract', contract });
    }
  }

  grouped.forEach((members, groupId) => {
    if (members.length === 1) {
      items.push({ kind: 'contract', contract: members[0] });
      return;
    }
    items.push({
      kind: 'group',
      groupId,
      members: members.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    });
  });

  return items.sort((a, b) => {
    const dateA =
      a.kind === 'contract'
        ? new Date(a.contract.createdAt).getTime()
        : Math.max(...a.members.map((m) => new Date(m.createdAt).getTime()));
    const dateB =
      b.kind === 'contract'
        ? new Date(b.contract.createdAt).getTime()
        : Math.max(...b.members.map((m) => new Date(m.createdAt).getTime()));
    return dateB - dateA;
  });
};

export const getItemContractIds = (item: HubListItem): string[] =>
  item.kind === 'contract' ? [item.contract.id] : item.members.map((m) => m.id);

export const contractMatchesTextFilter = (
  contract: ClientContractHubContractDto,
  text: string
): boolean => {
  const q = text.trim().toLowerCase();
  if (!q) return true;
  return (
    contract.tipoContratacao.toLowerCase().includes(q) ||
    (contract.contractType?.toLowerCase().includes(q) ?? false) ||
    (contract.token?.toLowerCase().includes(q) ?? false) ||
    contract.id.toLowerCase().includes(q) ||
    (contract.groupId?.toLowerCase().includes(q) ?? false)
  );
};

export const contractMatchesTipoFilter = (
  contract: ClientContractHubContractDto,
  tipo?: string
): boolean => !tipo || contract.tipoContratacao === tipo;

/** `'all'` = sem filtro; array vazio também trata como todos. */
export type HubCurrentStatusFilter = EContractStatus[] | 'all';

export const DEFAULT_HUB_STATUS_FILTER: HubCurrentStatusFilter = [EContractStatus.Ativo];

export const contractMatchesStatusesFilter = (
  contract: ClientContractHubContractDto,
  statuses: HubCurrentStatusFilter
): boolean => {
  if (statuses === 'all' || statuses.length === 0) return true;
  const contractStatus = normalizeContractStatus(
    contract.status as number | string | null | undefined
  );
  if (contractStatus === null) return false;
  return statuses.some(
    (s) => normalizeContractStatus(s) === contractStatus
  );
};

export const hubStatusFilterLabel = (statuses: HubCurrentStatusFilter): string => {
  if (statuses === 'all' || statuses.length === 0) return 'Todos os status';
  if (statuses.length === 1 && statuses[0] === EContractStatus.Ativo) return 'Só Ativos';
  return `${statuses.length} status selecionado(s)`;
};

export const isHubAtivoOnlyFilter = (statuses: HubCurrentStatusFilter): boolean =>
  statuses !== 'all' &&
  statuses.length === 1 &&
  statuses[0] === EContractStatus.Ativo;

const statusEventTypes = new Set([
  'StatusChange',
  'StatusSynthetic',
  'StatusInferred',
]);

export const contractMatchesHistoryAnd = (
  contractId: string,
  requiredStatuses: EContractStatus[],
  timeline: ClientContractHubTimelineEventDto[],
  currentStatus?: EContractStatus | string | number | null
): boolean => {
  if (requiredStatuses.length === 0) return true;

  const seen = new Set<number>();
  const current = normalizeContractStatus(currentStatus as number | string | null | undefined);
  if (current !== null) seen.add(current);

  for (const event of timeline) {
    if (event.contractId !== contractId || !statusEventTypes.has(event.eventType)) continue;
    const ev = normalizeContractStatus(event.eventStatus as number | string | null | undefined);
    const prev = normalizeContractStatus(event.previousStatus as number | string | null | undefined);
    if (ev !== null) seen.add(ev);
    if (prev !== null) seen.add(prev);
  }

  return requiredStatuses.every((s) => {
    const n = normalizeContractStatus(s);
    return n !== null && seen.has(n);
  });
};

export const filterHubListItems = (
  items: HubListItem[],
  opts: {
    text: string;
    statuses: HubCurrentStatusFilter;
    tipo?: string;
    historyStatuses: EContractStatus[];
    timeline: ClientContractHubTimelineEventDto[];
  }
): HubListItem[] => {
  const result: HubListItem[] = [];

  for (const item of items) {
    if (item.kind === 'contract') {
      const c = item.contract;
      if (
        contractMatchesTextFilter(c, opts.text) &&
        contractMatchesStatusesFilter(c, opts.statuses) &&
        contractMatchesTipoFilter(c, opts.tipo) &&
        contractMatchesHistoryAnd(c.id, opts.historyStatuses, opts.timeline, c.status)
      ) {
        result.push(item);
      }
      continue;
    }

    const matchingMembers = item.members.filter(
      (c) =>
        contractMatchesTextFilter(c, opts.text) &&
        contractMatchesStatusesFilter(c, opts.statuses) &&
        contractMatchesTipoFilter(c, opts.tipo) &&
        contractMatchesHistoryAnd(c.id, opts.historyStatuses, opts.timeline, c.status)
    );

    if (matchingMembers.length === 0) continue;
    if (matchingMembers.length === 1) {
      result.push({ kind: 'contract', contract: matchingMembers[0] });
    } else {
      result.push({ kind: 'group', groupId: item.groupId, members: matchingMembers });
    }
  }

  return result;
};

export const summarizeContracts = (contracts: ClientContractHubContractDto[]) => {
  const totalLines = contracts.reduce((s, c) => s + c.lineCount, 0);
  const totalValor = contracts.reduce((s, c) => s + c.valorTotalMensal, 0);
  const createdDates = contracts.map((c) => new Date(c.createdAt).getTime());
  const signedDates = contracts
    .filter((c) => c.dataAssinatura)
    .map((c) => new Date(c.dataAssinatura!).getTime());

  return {
    count: contracts.length,
    totalLines,
    totalValor,
    earliestCreated: createdDates.length
      ? new Date(Math.min(...createdDates)).toISOString()
      : undefined,
    latestCreated: createdDates.length
      ? new Date(Math.max(...createdDates)).toISOString()
      : undefined,
    latestSigned: signedDates.length
      ? new Date(Math.max(...signedDates)).toISOString()
      : undefined,
    fastchipCount: contracts.filter((c) => c.fastchip).length,
    cancelledCount: contracts.filter(
      (c) =>
        normalizeContractStatus(c.status as number | string | null | undefined) ===
        EContractStatus.Cancelado
    ).length,
  };
};

export const isClientLevelEvent = (event: ClientContractHubTimelineEventDto) =>
  !event.contractId || event.contractId === EMPTY_CONTRACT_ID;

export const filterTimelineForScope = (
  timeline: ClientContractHubTimelineEventDto[],
  visibleContractIds: Set<string>,
  selectedContractIds: Set<string>
) => {
  const scopeIds =
    selectedContractIds.size > 0 ? selectedContractIds : visibleContractIds;

  return timeline
    .filter(
      (e) =>
        isClientLevelEvent(e) ||
        (e.contractId && scopeIds.has(e.contractId))
    )
    .sort(
      (a, b) =>
        new Date(b.occurredAtUtc).getTime() - new Date(a.occurredAtUtc).getTime()
    );
};
